import React, { useEffect, useState } from "react"
import types from "prop-types"
import * as API from "services/api"
import { categoryOptionShape } from "utils/propTypeShapes"
import { errorToast } from "shared/toast"
import BackfillReportableFieldContext from "../BackfillReportableFieldContext"

const BackfillReportableFieldContextProvider = ({ categories, children, organizationSubdomain }) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState()
  const [reportableFieldsByCategory, setReportableFieldsByCategory] = useState({})
  const [selectedReportableFieldId, setSelectedReportableFieldId] = useState()
  const [formsByCategory, setFormsByCategory] = useState({})
  const [selectedFormId, setSelectedFormId] = useState()
  const [applicableQuestionOptions, setApplicableQuestionOptions] = useState([])
  const [selectedQuestionUuid, setSelectedQuestionUuid] = useState()

  const deselectReportableField = () => setSelectedReportableFieldId(null)

  const deselectForm = () => setSelectedFormId(null)

  const deselectQuestion = () => setSelectedQuestionUuid(null)

  const deselectCategory = () => setSelectedCategoryId(null)

  const selectCategory = (categoryId) => {
    if (categoryId === selectedCategoryId) return

    deselectQuestion()
    deselectReportableField()
    deselectForm()
    setSelectedCategoryId(categoryId)
  }

  const selectReportableField = (reportableFieldId) => {
    if (reportableFieldId === selectedReportableFieldId) return

    deselectQuestion()
    setSelectedReportableFieldId(reportableFieldId)
  }

  const selectForm = (formId) => {
    if (formId === selectedFormId) return

    deselectQuestion()
    setSelectedFormId(formId)
  }

  const deselectSelections = () => {
    deselectQuestion()
    deselectReportableField()
    deselectForm()
    deselectCategory()
  }

  // Load reportable fields when a category is selected
  useEffect(() => {
    const updateCategoryReportableFields = async () => {
      if (!selectedCategoryId) return

      if (!reportableFieldsByCategory[selectedCategoryId]) {
        const response = await API.phpCategoryReportableFields({
          organizationSubdomain,
          categoryId: selectedCategoryId,
        })

        if (response.ok) {
          const updatedReportableFieldsByCategory = {
            ...reportableFieldsByCategory,
            [selectedCategoryId]: response.data,
          }

          setReportableFieldsByCategory(updatedReportableFieldsByCategory)
        } else {
          errorToast("Something went wrong. Unable to load reportable fields")
        }
      }
    }

    updateCategoryReportableFields()
  }, [organizationSubdomain, reportableFieldsByCategory, selectedCategoryId])

  // Load forms when a category is selected
  useEffect(() => {
    const updateCategoryForms = async () => {
      if (!selectedCategoryId) return

      if (!formsByCategory[selectedCategoryId]) {
        const response = await API.phpCategoryFormsForSubmitting({
          organizationSubdomain,
          categoryId: selectedCategoryId,
        })

        if (response.ok) {
          const updatedFormsByCategory = {
            ...formsByCategory,
            [selectedCategoryId]: response.data,
          }

          setFormsByCategory(updatedFormsByCategory)
        } else {
          errorToast("Something went wrong. Unable to load category forms")
        }
      }
    }

    updateCategoryForms()
  }, [formsByCategory, organizationSubdomain, selectedCategoryId])

  // Load applicable question options when a reportable field and form are selected
  useEffect(() => {
    const updateApplicableQuestions = async () => {
      let questions = []

      if (selectedCategoryId && selectedFormId && selectedReportableFieldId) {
        const response = await API.phpCategoryReportableFieldApplicableQuestionOptions({
          categoryId: selectedCategoryId,
          formId: selectedFormId,
          organizationSubdomain,
          reportableFieldId: selectedReportableFieldId,
        })

        if (response.ok) {
          questions = response.data
        } else {
          errorToast("Something went wrong. Unable to load question options")
        }
      }

      setApplicableQuestionOptions(questions)
    }

    updateApplicableQuestions()
  }, [organizationSubdomain, selectedCategoryId, selectedFormId, selectedReportableFieldId])

  const selectedCategoryReportableFields = reportableFieldsByCategory[selectedCategoryId] ?? []
  const selectedCategoryForms = formsByCategory[selectedCategoryId] ?? []

  const contextConsumerValue = {
    applicableQuestionOptions,
    categories,
    deselectSelections,
    organizationSubdomain,
    selectCategory,
    selectedCategoryId,
    selectedCategoryForms,
    selectedCategoryReportableFields,
    selectedFormId,
    selectedQuestionUuid,
    selectedReportableFieldId,
    selectForm,
    selectQuestion: setSelectedQuestionUuid,
    selectReportableField,
  }

  return (
    <BackfillReportableFieldContext.Provider value={contextConsumerValue}>
      {children}
    </BackfillReportableFieldContext.Provider>
  )
}

BackfillReportableFieldContextProvider.propTypes = {
  categories: types.arrayOf(categoryOptionShape).isRequired,
  children: types.node.isRequired,
  organizationSubdomain: types.string.isRequired,
}

export default BackfillReportableFieldContextProvider
